import React from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { Row, Container, Col } from 'reactstrap';
import ArticleCarousel, {
  loadCarouselArticles,
} from 'components/article/ArticleCarousel';

import domainModel from 'domain-model';
import Error from 'next/error';
import ArticleHeart from 'components/article/ArticleHeart';
import ImageGallery from 'flaivy-react/components/ImageGallery';
import ArticleQuantityInput from 'flaivy-react/components/cart/CartArticleQuantityInput';
import ArticleDetails from 'flaivy-react/components/article/ArticleDetails';
import FlavourCard from 'flaivy-react/components/article/FlavourCard';
import ArticleBadges from 'components/article/ArticleBadges';
import ArticleBreadCrumbs from 'components/ArticleBreadCrumbs';
import LoginToShowPrices from 'components/LoginToShowPrices';
import getAxiosInstance from 'app/config/api';

import { HeadDescriptionElements } from 'components/HeadElement';
import loginAwareComponent from '../../../components/LoginAwareComponent';
import ArticleImage from 'flaivy-react/components/article/ArticleImage';
import ShowMore from 'components/ShowMore';
import DiscountFlag from 'components/article/DiscountFlag';

function ArticlePage({
  pricedArticle,
  relatedArticlesCarousel,
  shopConfig,
  notFound,
}) {
  if (notFound) {
    return <Error statusCode={404} />;
  }
  const {
    image,
    shortName,
    description,
    goesWith,
    appearance,
    taste,
    scent,
    brand,
    productCategory,
    organizationProductCategory,
  } = pricedArticle;

  return (
    <>
      <HeadDescriptionElements
        description={description}
        image={image}
        title={shortName}
      />
      <Container className="p-5 articleDetailsPage" id="content">
        <Row>
          <Col xs={10}>
            <h1 className="text-capitalized">{shortName}</h1>
            <h4>{brand?.name}</h4>
            <ArticleBreadCrumbs
              productCategory={productCategory}
              organizationProductCategory={organizationProductCategory}
              shopConfig={shopConfig}
              className="text-sm"
            />
          </Col>
          <Col className="text-end">
            <ArticleHeart article={pricedArticle} size="2x" />
          </Col>
        </Row>
        <Row>
          <Col
            xs={12}
            md={5}
            className="text-center my-2 mt-md-4 position-relative"
          >
            <h4>
              <ArticleBadges article={pricedArticle} />
            </h4>
            {pricedArticle.image ? (
              <ImageGallery
                entity={pricedArticle}
                alt={shortName}
                resX={800}
                resY={800}
                className="img-fluid"
              />
            ) : (
              <ArticleImage
                article={pricedArticle}
                alt={shortName}
                resX={800}
                resY={800}
                className="img-fluid"
              />
            )}
            <DiscountFlag article={pricedArticle} />
          </Col>
          <Col
            xs={12}
            md={7}
            className="mb-2 d-flex justify-content-around flex-column"
          >
            <ShowMore
              maxHeight="300px"
              buttonPosition="center"
              className="mb-3"
            >
              <Row>
                <Col>
                  <div
                    suppressHydrationWarning
                    className="mt-4 text-muted"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                </Col>
              </Row>
              {(goesWith || appearance || scent || taste) && (
                <Row>
                  {goesWith && (
                    <FlavourCard title="Passar till" text={goesWith} />
                  )}
                  {appearance && (
                    <FlavourCard title="Utseende" text={appearance} />
                  )}
                  {scent && <FlavourCard title="Doft" text={scent} />}
                  {taste && <FlavourCard title="Smak" text={taste} />}
                </Row>
              )}
            </ShowMore>

            <Row className="justify-content-center">
              <Col className="text-center">
                {pricedArticle.active && (
                  <LoginToShowPrices className="border-0">
                    <ArticleQuantityInput article={pricedArticle} shadow />
                  </LoginToShowPrices>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
        {!domainModel.shopConfig.isWhitelabel(shopConfig) && (
          <ArticleDetails
            article={pricedArticle}
            shopConfig={shopConfig}
          ></ArticleDetails>
        )}
        {domainModel.shopConfig.isWhitelabel(shopConfig) && (
          <ArticleDetails
            article={_.omit(pricedArticle, 'supplier')}
            shopConfig={shopConfig}
          ></ArticleDetails>
        )}

        {!_.isEmpty(relatedArticlesCarousel.articles) && (
          <Row className="mt-4 justify-content-center">
            <Col key="relatedArticleCarousel">
              <ArticleCarousel
                carousel={relatedArticlesCarousel}
                leftAdjustTitle=""
              />
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
}

async function getCarouselData(articleViewModel, req) {
  const relatedArticlesDifferentBrandQuery = {
    productCategory: { iLike: `${articleViewModel.productCategory}%` },
    brand: { ne: _.get(articleViewModel, 'brand.id', 'DONT GIVE ME STUFF') },
    id: { ne: articleViewModel.id },
  };
  const relatedArticlesSameBrandQuery = {
    productCategory: { iLike: `${articleViewModel.productCategory}%` },
    brand: _.get(articleViewModel, 'brand.id', 'DONT GIVE ME STUFF'),
    id: { ne: articleViewModel.id },
  };

  if (articleViewModel.requiresAlcoholPermit) {
    relatedArticlesDifferentBrandQuery.requiresAlcoholPermit = true;
    relatedArticlesSameBrandQuery.requiresAlcoholPermit = true;
  }

  const relatedArticlesDifferentBrand = await loadCarouselArticles(
    { query: relatedArticlesDifferentBrandQuery },
    req,
  );

  const relatedArticlesSameBrand = await loadCarouselArticles(
    { query: relatedArticlesSameBrandQuery },
    req,
  );

  return {
    articles: _.flatten(
      _.zip(relatedArticlesSameBrand, relatedArticlesDifferentBrand),
    ).filter(x => !!x),
    config: { name: 'Liknande artiklar', query: {} },
  };
}

ArticlePage.getInitialProps = async props => {
  const { req, res, query, authentication, shopConfig } = props;
  const articleId = _.get(query, 'id');
  let response;
  try {
    response = await getAxiosInstance(req).get(`public/articles/${articleId}`);
  } catch (error) {
    console.log('articleId', articleId, 'error', error);
    if (error.response.status === 404) {
      if (res) {
        res.statusCode = 404;
      }
      return { shopConfig, query, authentication, notFound: true };
    }
    if (error.response.status === 403) {
      if (res) {
        res.statusCode = 403;
      }
      return { shopConfig, query, authentication, notFound: true };
    }
  }

  const articleViewModel = domainModel.pricedArticle.getViewModel(
    response.data,
  );

  if (!articleViewModel.productCategory) {
    return {
      shopConfig,
      query,
      authentication,
      pricedArticle: articleViewModel,
      relatedArticlesCarousel: { articles: [], config: {} },
    };
  }
  try {
    const relatedArticlesCarousel = await getCarouselData(
      articleViewModel,
      req,
    );
    return {
      shopConfig,
      query,
      authentication,
      pricedArticle: articleViewModel,
      relatedArticlesCarousel,
    };
  } catch (e) {
    console.log('articleId', articleId, 'error', e);
    return {
      shopConfig,
      query,
      authentication,
      pricedArticle: articleViewModel,
      relatedArticlesCarousel: { articles: [], config: {} },
    };
  }
};

ArticlePage.propTypes = {
  pricedArticle: PropTypes.object.isRequired,
  shopConfig: PropTypes.object.isRequired,
};

const mapStateToProps = ({ authentication, shopConfig }) => ({
  authentication,
  shopConfig,
});
export default connect(mapStateToProps)(loginAwareComponent(ArticlePage));
